import { Injectable } from '@angular/core';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Observable, OperatorFunction } from 'rxjs';
import { isEqual } from 'lodash-es';
import { ReportingUserSessionsService } from '@nimbus-global/features/reportingLiveData/reporting-user-sessions.service';
import { InitialModality } from '@nimbus-library/features/shared/models/participant-info/i-combined-conversation.response';
import { UserPreferencesNotificationsState } from '@nimbus-library/features/user-settings/store/user-preferences-notifications.state';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { BaseUserPreferencesNotificator } from '@nimbus-library/features/user-settings/services/user-preferences-notification-sender/user-preferences-notificators/base/base-user-preferences-notificator.service';
import { IUserSessionLive } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import { UserPreferencesNotificationAudioService } from '@nimbus-library/features/user-settings/services/user-preferences-notification-sender/user-preferences-notification-audio.service';
import { UserPreferencesNotificationsModel } from '@nimbus-library/features/user-settings/models/user-preferences-notifications.model';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class UserPreferencesNotificatorByEmailService extends BaseUserPreferencesNotificator {
  protected audioType: string = 'Email';

  @Select(UserPreferencesNotificationsState.notificationsModel)
  public notificationsModel$: Observable<UserPreferencesNotificationsModel>;

  public constructor(
    private readonly router: Router,
    protected readonly userPreferencesNotificationAudioService: UserPreferencesNotificationAudioService,
    private readonly reportingUserSessionsService: ReportingUserSessionsService,
    private readonly translateService: TranslateService,
  ) {
    super(userPreferencesNotificationAudioService);
  }

  public initialize(): void {
    combineLatest([
      this.notificationsModel$.pipe(distinctUntilChanged(isEqual)),
      this.reportingUserSessionsService.getUserSessionsLiveIncoming$().pipe(this.mapEmails()),
    ])
      .pipe(
        tap(([notificationsModel, items]) => {
          const playAudio = notificationsModel.useAudioForEmails && !!items.length;
          this.toggleAudioNotification(playAudio, notificationsModel.audioForEmails);

          if (notificationsModel.useDesktopNotificationForEmails) {
            items.forEach((x) =>
              this.showDesktopNotification(
                x.id,
                this.translateService.instant(TranslationKeys.UserPreferencesNotificationsMessagesEmailTitle),
                x.teamInformation?.serviceName ||
                  this.translateService.instant(TranslationKeys.UserPreferencesNotificationsMessagesEmailBody),
                (): void => {
                  this.router.navigateByUrl('personal/my-sessions').then();
                },
              ),
            );
          }
        }),
        untilDestroyed(this),
      )
      .subscribe();
    this.reportingUserSessionsService
      .getUserSessionsLiveCompleted$()
      .pipe(
        this.mapEmails(),
        tap((items) => items.forEach((x) => this.removeIdNotified(x.id))),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private mapEmails<T extends IUserSessionLive>(): OperatorFunction<T[], T[]> {
    return map((items) => items.filter((x) => x.initialModality === InitialModality.Email));
  }
}
